import {
  Button,
  column,
  Css,
  dateColumn,
  getTableStyles,
  GridColumn,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps,
  simpleDataRows,
  simpleHeader,
  SimpleHeaderAndData,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { useMemo } from "react";
import { dateCell } from "src/components";
import {
  MergeHistoryModal_MaterialVariantFragment,
  useMergeHistoryModalQuery,
} from "src/routes/libraries/material-catalog/components/merge-history-modal/MergeHistoryModal.generated";
import { queryResult } from "src/utils";

type MergeHistoryModalProps = { materialVariantId: string };

type MergeRow = MergeHistoryModal_MaterialVariantFragment["merges"][number];
type Row = SimpleHeaderAndData<MergeRow>;

export const mergeHistoryModalOptions: Pick<ModalProps, "size" | "drawHeaderBorder"> = {
  size: "lg",
  drawHeaderBorder: true,
};

const tableStyles = getTableStyles({
  allWhite: true,
  bordered: true,
  rowHeight: "flexible",
  vAlign: "top",
  rowHover: false,
});
const tableStyle = { ...tableStyles, rootCss: { ...tableStyles.rootCss, ...Css.w100.$ } };

export function MergeHistoryModal(props: MergeHistoryModalProps) {
  const { materialVariantId } = props;
  const { closeModal } = useModal();
  const query = useMergeHistoryModalQuery({ variables: { id: materialVariantId } });

  return (
    <>
      <ModalHeader>Merge History</ModalHeader>
      <ModalBody>
        {queryResult(query, { data: (data) => <MergeHistoryList materialVariant={data.materialVariant} /> })}
      </ModalBody>
      <ModalFooter>
        <Button label="Ok" onClick={closeModal} />
      </ModalFooter>
    </>
  );
}

type MergeHistoryListProps = { materialVariant: MergeHistoryModal_MaterialVariantFragment };

function MergeHistoryList(props: MergeHistoryListProps) {
  const { materialVariant } = props;
  const { merges, id: currentId, displayName, code } = materialVariant;
  const tid = useTestIds({});
  const sortedMerges = useMemo(() => merges.sortBy((m) => m.createdAt.epochMilliseconds).reverse(), [merges]);
  const intoNamesById = useMemo(() => buildIntoNamesById(merges, materialVariant), [merges, materialVariant]);
  const columns = useMemo(() => createColumns(currentId, intoNamesById, tid), [currentId, intoNamesById, tid]);

  if (sortedMerges.isEmpty) {
    return (
      <div css={Css.gray700.my3.$} {...tid.emptyMergeHistory}>
        No merge history for {displayName}.
      </div>
    );
  }

  return (
    <div {...tid.mergeHistoryList}>
      <div css={Css.mb3.$} {...tid.currentVariant}>
        <div css={Css.lg.fw6.$}>{displayName}</div>
        {code && <div css={Css.gray600.sm.$}>{code}</div>}
      </div>
      <div css={Css.gray600.sm.mb3.$}>Previous material variants merged into this one:</div>
      <GridTable columns={columns} rows={[simpleHeader, ...simpleDataRows(sortedMerges)]} style={tableStyle} />
    </div>
  );
}

function buildIntoNamesById(
  merges: MergeRow[],
  current: MergeHistoryModal_MaterialVariantFragment,
): Map<string, string> {
  const names = new Map<string, string>();
  names.set(current.id, current.displayName);
  merges.forEach((merge) => {
    names.set(merge.mergedFromId, merge.mergedFromDisplayName || merge.mergedFromCode);
  });
  return names;
}

function createColumns(
  currentId: string,
  intoNamesById: Map<string, string>,
  tid: ReturnType<typeof useTestIds>,
): GridColumn<Row>[] {
  return [
    column<Row>({
      header: "Merged From",
      w: "2fr",
      data: (merge) => {
        const name = merge.mergedFromDisplayName || merge.mergedFromCode;
        return {
          content: () => (
            <div css={Css.whiteSpace("normal").$} {...tid.mergedFrom}>
              {name}
            </div>
          ),
          value: name,
        };
      },
    }),
    column<Row>({
      header: "Merged Into",
      w: "2fr",
      data: (merge) => {
        const isCurrent = merge.mergedIntoId === currentId;
        const name = intoNamesById.get(merge.mergedIntoId) ?? "Unknown variant";
        return {
          content: () =>
            isCurrent ? (
              <div css={Css.fw7.whiteSpace("normal").$} {...tid.mergedInto}>
                This variant
              </div>
            ) : (
              <div css={Css.whiteSpace("normal").$} {...tid.mergedInto}>
                {name}
              </div>
            ),
          value: isCurrent ? "This variant" : name,
        };
      },
    }),
    column<Row>({
      header: "Merged By",
      w: "1fr",
      data: (merge) => {
        const mergedBy = merge.mergedBy?.name;
        return { content: () => <span {...tid.mergedBy}>{mergedBy ?? "---"}</span>, value: mergedBy ?? "---" };
      },
    }),
    dateColumn<Row>({
      header: "Date",
      w: "140px",
      data: ({ createdAt }) => dateCell(createdAt, { dateFormatStyle: "long" }),
    }),
  ];
}
